/**
 * Image URLs.
 *
 * With a Cloudinary cloud configured (VITE_CLOUDINARY_CLOUD_NAME), every
 * picture is served from Cloudinary's CDN in the right size and the best
 * format the browser accepts (f_auto, q_auto). Without one — a laptop with
 * no keys — the bundled crops in /public/img are used unchanged.
 *
 * The site's own photographs live under `dahaeaem/site/<name>`; menu photos
 * uploaded from the dashboard under `dahaeaem/menu/…`.
 */

const CLOUD = (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined)?.trim();
const BASE = CLOUD ? `https://res.cloudinary.com/${CLOUD}/image/upload/` : "";

export type SiteImageName = "interior" | "banquette" | "ceramics" | "parquet" | "toast" | "drink" | "guests" | "sofa" | "corner" | "mirror";

// The names are the slots on the page, not the subject. The four photographs
// from dahaeaem_webbilder_v1 fill five (interior, sitzecke → banquette +
// ceramics, cocktail → drink, materialdetail → parquet); the café's platter
// photos fill the other two (toast → the veggie board, guests → two boards).
const SITE_IMAGES: Record<SiteImageName, string> = {
  interior: "interior",
  banquette: "banquette",
  ceramics: "ceramics",
  parquet: "parquet",
  toast: "veggieboard",
  drink: "drink",
  // The manifesto: the sofa corner (owner: no food photo next to the quote).
  guests: "corner",
  // The room, photographed 3 Nov 2026: sofa wall, sofa corner, mirror wall.
  sofa: "sofa",
  corner: "corner",
  mirror: "mirror",
};

function transform(width: number, extra = ""): string {
  return `f_auto,q_auto,c_limit,w_${width}${extra ? `,${extra}` : ""}/`;
}

// Bump when a photograph is replaced: the CDN tells browsers to keep an image
// for 30 days, and only a new URL makes them fetch the new picture.
const SITE_IMAGE_VERSION = "v20261105";

/** One of the site's own photographs at a given width. */
export function siteImage(name: SiteImageName, width = 1200): string {
  if (!CLOUD) return `/img/${SITE_IMAGES[name]}.jpg`;
  return `${BASE}${transform(width)}${SITE_IMAGE_VERSION}/dahaeaem/site/${SITE_IMAGES[name]}`;
}

/**
 * Any stored image reference at a given width: a Cloudinary URL (transforms
 * inserted), a bundled `/img/<name>.jpg` (mapped to the same photo on
 * Cloudinary when configured), or anything else untouched.
 */
export function imageUrl(src: string | null | undefined, width = 800): string {
  if (!src) return "";
  const bundled = /^\/img\/([a-z]+)\.jpg$/.exec(src);
  if (bundled && bundled[1] in SITE_IMAGES) return siteImage(bundled[1] as SiteImageName, width);
  if (!CLOUD) return src;
  const marker = "/image/upload/";
  const at = src.indexOf(marker);
  if (at === -1 || !src.includes(`res.cloudinary.com/${CLOUD}/`)) return src;
  const rest = src.slice(at + marker.length);
  // Drop a transformation that is already there so ours is the only one.
  const path = /^v\d+\//.test(rest) || !rest.includes("/") ? rest : rest.slice(rest.indexOf("/") + 1);
  return `${src.slice(0, at + marker.length)}${transform(width)}${path}`;
}

/** A srcset over a few widths, for `<img sizes>` to pick from. */
export function srcSet(src: string | null | undefined, widths: number[]): string | undefined {
  if (!src || !CLOUD) return undefined;
  const isCloud = src.startsWith("/img/") || src.includes("res.cloudinary.com/");
  if (!isCloud) return undefined;
  return widths.map((w) => `${imageUrl(src, w)} ${w}w`).join(", ");
}
